// External
import { useMutation, useQueryClient } from '@tanstack/react-query';

// Hooks and utilities
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../useTranslation';

// API
import {
  createBillingAddress,
  updateBillingAddress,
  deleteBillingAddress,
  setDefaultBillingAddress,
  contactsQueryKeys,
} from '@api/contacts';
import type { ApiContact, CreateBillingAddressPayload } from '@api/contacts';

// Each endpoint returns the full updated contact, so we prime the detail cache
// from the response and let subscribers re-render without a refetch.
export function useContactBillingAddresses(contactId: number) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const primeDetail = (contact: ApiContact) => {
    queryClient.setQueryData(contactsQueryKeys.detail(contactId), { contact });
  };

  const create = useMutation({
    mutationFn: (payload: CreateBillingAddressPayload) => createBillingAddress(contactId, payload),
    onSuccess: (contact) => {
      primeDetail(contact);
      toast({ description: t('billing_addresses.updated') });
    },
    onError: () => {
      toast({ description: t('billing_addresses.add_error'), variant: 'destructive' });
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CreateBillingAddressPayload }) =>
      updateBillingAddress(contactId, id, payload),
    onSuccess: (contact) => {
      primeDetail(contact);
      toast({ description: t('billing_addresses.updated') });
    },
    onError: () => {
      toast({ description: t('billing_addresses.update_error'), variant: 'destructive' });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteBillingAddress(contactId, id),
    onSuccess: (contact) => {
      primeDetail(contact);
      toast({ description: t('billing_addresses.removed') });
    },
    onError: () => {
      toast({ description: t('billing_addresses.remove_error'), variant: 'destructive' });
    },
  });

  const setDefault = useMutation({
    mutationFn: (id: number) => setDefaultBillingAddress(contactId, id),
    onSuccess: (contact) => {
      primeDetail(contact);
      toast({ description: t('billing_addresses.updated') });
    },
    onError: () => {
      toast({ description: t('billing_addresses.default_error'), variant: 'destructive' });
    },
  });

  return { create, update, remove, setDefault };
}
